import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";

import { ROUTES } from "~/constants/routing";

interface IAppNavigationHistory {
  currentPath: string;
  previousPath: string | null;
}

interface IAppNavigationContextValue extends IAppNavigationHistory {
  goBackToPreviousAppPage: () => void;
}

const AppNavigationContext = createContext<IAppNavigationContextValue | null>(
  null
);

const getLocationPath = (location: ReturnType<typeof useLocation>) =>
  `${location.pathname}${location.search}`;

export const AppNavigationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const nextPath = getLocationPath(location);

  const [history, setHistory] = useState<IAppNavigationHistory>({
    currentPath: nextPath,
    previousPath: null,
  });

  const currentHistory = useMemo(
    () =>
      history.currentPath === nextPath
        ? history
        : {
            currentPath: nextPath,
            previousPath: history.currentPath,
          },
    [history, nextPath]
  );

  if (history.currentPath !== nextPath) {
    setHistory(currentHistory);
  }

  const goBackToPreviousAppPage = useCallback(() => {
    navigate(currentHistory.previousPath || ROUTES.main.createURL());
  }, [currentHistory.previousPath, navigate]);

  const value = useMemo(
    () => ({
      ...currentHistory,
      goBackToPreviousAppPage,
    }),
    [currentHistory, goBackToPreviousAppPage]
  );

  return (
    <AppNavigationContext.Provider value={value}>
      {children}
    </AppNavigationContext.Provider>
  );
};

export const useAppNavigation = (): IAppNavigationContextValue => {
  const context = useContext(AppNavigationContext);

  if (!context) {
    throw new Error(
      "useAppNavigation must be used within AppNavigationProvider"
    );
  }

  return context;
};
